import { EExpandedOrganizationRoles } from './IOrganization';
import { EUserPrivacy } from './IRoleManager';

export interface IModuleMember {
    role: number | string;
    isDeleted: boolean;
    firstName: string;
    lastName: string;
    jobTitle: string;
    department: string;
    picture: string;
    cabinet: string;
    id: string;
}

export interface IModuleRole {
    role: number;
    roleName: string;
}

export interface IRole {
    id: string;
    name: string;
    resourceType: number;
    actions: string[];
    childId: string;
}

export interface IAvailableRole {
    role: number;
    roleName: string;
    id: string;
}

export interface IPrincipal {
    principalType: EPrincipalTypeRole;
    privacy?: EUserPrivacy;
    id: string;
    childId: string;
    childName: string;
    picture: string;
    type: EPrincipalType;
}

export interface IPrincipal {
    id: string;
    childId: string;
    childName: string;
    picture: string;
    roleName: string;
    roleId: string;
}
export interface IAssignedPrincipal {
    principals: IPrincipal[];
    permissions: string[];
}

export interface IResource {
    id: string;
    resourceType: number;
    childId: string;
    childName: string;
    canRequestAccess: boolean;
    isInvitable: boolean;
}

export interface IAssignedRole {
    id: string;
    principal: IPrincipal;
    resource: IResource;
    role: IRole;
    isDeleted: boolean;
}

export interface IGetInvitation {
    date: string | Date;
    id: string;
    message?: string;
    receiver: {
        cabinet: string;
        department: string;
        email: string;
        firstName: string;
        id: string;
        jobTitle: string;
        lastName: string;
        picture: string;
    };
    resource: {
        canRequestAccess: boolean;
        childId: string;
        childName: string;
        id: string;
        isInvitable: string;
        metadata: {};
        parentId: string;
        resourceType: 'Organization' | 'Cluster';
    }
    role: string;
    sender: {
        cabinet: string;
        department: string;
        firstName: string;
        id: string;
        jobTitle: string;
        lastName: string;
        picture: string;
    };
    status: number;
}

export interface IGetMultipleGroupsById {
    description: string,
    groupUsers: {
        cabinet: string;
        id: string;
        jobTitle: string;
        name: string;
        picture: string;
        role: {
            actions: string[];
            childId: string;
            id: string;
            isHidden: boolean;
            name: string;
            resourceType: number;
        };
        type: number
    }[];
    id: string;
    isEditable: boolean;
    isHidden: boolean;
    name: string;
}

export enum EPrincipalType {
    Personal = 0,
    Group = 1
}

export enum EPrincipalTypeRole {
    Group = 'Group',
    Personal = 'Personal'
}

export enum EGroupResourceType {
    CLUSTER = 'Cluster',
    ORGANIZATION = 'Organization'
}

export enum ERoleType {
    Organization = 'Organization',
    Cluster = 'Cluster',
    ClusterChannel = 'ClusterChannel',
    Panel = 'Panel',
    Module = 'Module',
    Platform = 'Platform',
    OrganizationsModule = 'OrganizationsModule',
    ReferentialsModule = 'ReferentialsModule',
    PublicationsModule = 'PublicationsModule',
    UsersModule = 'UsersModule',
    TranslationsModule = 'TranslationsModule',
    Group = 'Group',
    Personal = 'Personal'
}

export enum EResourcePermissions {
    ManageMembers = 'Resource.Members.Manage',
    MembersView = 'Resource.Members.View',
    RequestAccess = 'Clusters.Privileges.RequestAccess',
    ResourceMembersJoin = 'Resource.Members.Join',
    OrganizationsJoinToClusterAsOrganizationOwner = 'Organizations.JoinToCluster.AsOrganizationOwner',
    OrganizationsJoinToClusterAsOrganizationMember = 'Organizations.JoinToCluster.AsOrganizationMember',
    OrganizationsJoinToClusterAsOrganizationGuest = 'Organizations.JoinToCluster.AsOrganizationGuest'
}

/** Permissions that allow joining a cluster. Organizations* take precedence over ResourceMembersJoin when choosing the join action. */
export const JOIN_CLUSTER_PERMISSIONS: readonly EResourcePermissions[] = [
    EResourcePermissions.OrganizationsJoinToClusterAsOrganizationOwner,
    EResourcePermissions.OrganizationsJoinToClusterAsOrganizationMember,
    EResourcePermissions.OrganizationsJoinToClusterAsOrganizationGuest,
    EResourcePermissions.ResourceMembersJoin
] as const;

/** Returns the permission to use for the join action. Organizations* permissions take precedence over ResourceMembersJoin. */
export function getEffectiveJoinPermission(permissions: string[] | Record<string, boolean> | undefined): EResourcePermissions | null {
    if (!permissions) return null;
    const has = (p: string) =>
        Array.isArray(permissions) ? (permissions as string[]).includes(p) : !!(permissions as Record<string, boolean>)[p];
    for (const perm of JOIN_CLUSTER_PERMISSIONS) {
        if (has(perm)) return perm;
    }
    return null;
}

/** True if the user has any permission that allows joining a cluster. */
export function hasJoinClusterPermission(permissions: string[] | Record<string, boolean> | undefined): boolean {
    return getEffectiveJoinPermission(permissions) !== null;
}

export enum EDocumentsPermissions {
    CopyLink = 'Clusters.Documents.CopyLink',
    Download = 'Clusters.Documents.Download',
    AskApproval = 'Clusters.Documents.AskApproval',
    AskSignature = 'Clusters.Documents.AskSignature',
    EditViaWebBrowser = 'Clusters.Documents.EditViaWebBrowser',
    Rename = 'Clusters.Documents.Rename',
    Description = 'Clusters.Documents.Description',
    Copy = 'Clusters.Documents.Copy',
    Move = 'Clusters.Documents.Move',
    Restore = 'Clusters.Documents.Restore',
    Delete = 'Clusters.Channels.Delete',
    DeletePermenantly = 'Clusters.Documents.Delete.Permenantly',
    DeleteTemporarily = 'Clusters.Documents.Delete.Temporarily',
    View = 'Clusters.Documents.View',
    FileLibrary = 'Clusters.Documents.Upload.FileLibrary',
    Upload = 'Clusters.Documents.Upload'
}

export enum EClusterPermissionsBase {
    ViewCluster = 'Clusters.View',
    ViewDatagid = 'Clusters.View.Datagrid',
    ClusterPreview = 'Cluster.Preview',
    EditCluster = 'Clusters.Edit',
    ClustersGiftInvitationEdit = 'Clusters.GiftInvitation.Edit',
    SetOrganizationalData = 'Clusters.Organization.Data.Set',
    ViewOrganizationData = 'Clusters.Organization.Data.View',
    CreateChannel = 'Clusters.Channels.Create',
    CreatePrivateChannels = 'Clusters.PrivateChannels.Create',
    DeleteChannel = 'Clusters.Channels.Delete',
    UploadDocuments = 'Clusters.Documents.Upload',
    ResourceMembersRequestAccess = 'Resource.Members.RequestAccess',
    ViewDocuments = 'Clusters.Documents.View',
    UploadDocumentsInConversation = 'Clusters.Documents.Upload.Conversation',
    UploadDocumentsInFileLibrary = 'Clusters.Documents.Upload.FileLibrary',
    DeleteTemporarilyDocuments = 'Clusters.Documents.Delete.Temporarily',
    DeletePermenantlyDocuments = 'Clusters.Documents.Delete.Permenantly',
    ClusterDataExport = 'Cluster.Data.Export',
    ClustersEditInViewMode = 'Clusters.Edit.InViewMode',
    ConversationMessagesView = 'Teams.Channels.Conversations.Threads.Messages.View',
    ConversationMessagesCreate = 'Teams.Channels.Conversations.Threads.Messages.Create',
    ConversationMessagesDelete = 'Teams.Channels.Conversations.Threads.Messages.Delete',
    ClusterDuplicate = 'Cluster.Duplicate',
    ClusterLinksView = 'ClusterLinks.View',
    ClusterLinksEdit = 'ClusterLinks.Edit',
    ClusterDelete = 'Cluster.Delete',
    ClusterQuestionDelete = 'Cluster.Question.Delete',
    ApprovalDisable = 'Approval.Disable',
    ClustersActivityView = 'Clusters.Activity.View',
    ClustersConversationView = 'Clusters.Conversation.View',
    ClustersConversationCreate = 'Clusters.Conversation.Create',
    ClustersConversationUpdate = 'Clusters.Conversation.Update',
    ClustersConversationDelete = 'Clusters.Conversation.Delete',
    ClusterAskApproval = 'Cluster.AskApproval',
    ClustersDocumentsGenerateDocument = 'Clusters.Documents.GenerateDocument',
    ClustersViewActionsTab = 'Clusters.ViewActionsTab'

}

export enum EOrganizationPermissionsBase {
    ViewOrganization = 'Organizations.View',
    ViewClusters = 'Clusters.View.All',
    CreateCluster = 'Clusters.Create',
    ManageOrganization = 'Organizations.Manage',
    ReferentialsVisibility = 'Organizations.Referentials.ManageVisibility',
    ViewLinks = 'Organizations.Links.View',
    JoinPublicCluster = 'Clusters.Privileges.Join.Public',
    ClustersPrivilegesJoinSecret = 'Clusters.Privileges.Join.Secret',
    JoinAnyCluster = 'Clusters.Privileges.Join',
    OrganizationsAccountsView = 'Organizations.Accounts.View'
}

export enum EApprovalModulePermissions {
    Create = 'Approval.Create',
    View = 'Approval.View'
}

export enum ESignatureModulePermissions {
    Create = 'Signature.Create',
    View = 'Signature.View'
}

export enum EKeyDatesModulePermissions {
    ViewAll = 'KeyDates.ViewAll'
}

export enum ERestrictedSearchPermissions {
    ProfilePermissionsView = 'RestrictedSearch.Profile.Permissions.View'
}

export enum EPlatformPermissions {
    PlatformManagementView = 'Platform.Management.View',
    OrganizationsPrivilegesJoinAsOwner = 'Organizations.Privileges.JoinAsOwner',
    OrganizationsViewAll = 'Organizations.View.All',
    OrganizationsCreate = 'Organizations.Create',
    OrganizationsManageAll = 'Organizations.Manage.All',
    ResourceMembersManage = 'Resource.Members.Manage',
    ResourceMembersView = 'Resource.Members.View',
    GroupsCreate = 'Groups.Create',
    GroupsViewAll = 'Groups.View.All',
    ReferentialsView = 'Referentials.View',
    AssociationsView = 'Associations.View',
    AssociationsAdd = 'Associations.Add',
    AssociationsDelete = 'Associations.Delete',
    AssociationsEdit = 'Associations.Edit',
    SchoolView = 'School.View',
    SchoolAdd = 'School.Add',
    SchoolEdit = 'School.Edit',
    JurisdictionView = 'Jurisdiction.View',
    JurisdictionAdd = 'Jurisdiction.Add',
    JurisdictionEdit = 'Jurisdiction.Edit',
    ReferentialsClientManageItems = 'Referentials.Client.Manage.Items',
    ReferentialsClientManageLabel = 'Referentials.Client.Manage.Label',
    ReferentialsLegalEntityManage = 'Referentials.LegalEntity.Manage',
    ReferentialsLegalclusterManageItems = 'Referentials.Legalcluster.Manage.Items',
    ReferentialsLegalclusterManageLabel = 'Referentials.Legalcluster.Manage.Label',
    ReferentialsNormManageItems = 'Referentials.Norm.Manage.Items',
    ReferentialsNormManageLabel = 'Referentials.Norm.Manage.Label',
    TranslationsManage = 'Translations.Manage',
    UsefulLinksCreate = 'UsefulLinks.Create',
    UsefulLinksManageAll = 'UsefulLinks.Manage.All',
    UsersOnBehalfEdit = 'Users.OnBehalf.Edit',
    UserEditManager = 'User.Edit.Manager',
    UsersOnBehalfEditManager = 'Users.OnBehalf.Edit.Manager',
    UsersOnBehalfChangeEmail = 'Users.OnBehalf.ChangeEmail',
    LegalScoreOnBehalfRequest = 'LegalScore.OnBehalf.Request',
    InvitationsOnBehalfInvite = 'Invitations.OnBehalf.Invite',
    ArticlesView = 'Articles.View',
    ArticlesViewMine = 'Articles.View.Mine',
    ArticlesCreate = 'Articles.Create',
    ArticlesPublish = 'Articles.Publish',
    ArticlesPromote = 'Articles.Promote',
    ArticlesOnBehalfView = 'Articles.OnBehalf.View',
    ArticlesOnBehalfCreate = 'Articles.OnBehalf.Create',
    ArticlesOnBehalfDelete = 'Articles.OnBehalf.Delete',
    ArticlesOnBehalfPublish = 'Articles.OnBehalf.Publish',
    UsersDirectoryGlobal = 'Users.Directory.Global',
    UsersDirectoryMyNetwork = 'Users.Directory.MyNetwork',
    UsersOther = 'Users.Other',
    UsersSearch = 'Users.Search',
    CompaniesAdd = 'Companies.Add',
    CompaniesView = 'Companies.View',
    CompaniesDataGridView = 'Companies.DataGrid.View',
    CompaniesDataGridEdit = 'Companies.DataGrid.Edit',
    UsefulLinksView = 'UsefulLinks.View',
    LegalScoreRequest = 'LegalScore.Request',
    LegalScoreTrigger = 'LegalScore.Trigger',
    PlatformEntitiesView = 'Companies.Search'
}

export interface IPrincipalPermissionRole {
    roleCode?: number;
    roleId?: string;
    roleChildId?: string;
    roleName?: string;
    roleNameTranslationKey?: string;
    isHidden?: boolean;
}

export interface IPrincipalPermissionType {
    enumElement?: string;
    translatedValue?: string;
    translationKey?: string;
    value?: string;
}

export interface IPrincipalPermissionResource {
    businessId?: number;
    strBusinessId?: string;
    resourceType?: number;
    resourceTypeValue?: string;
    resourceChildId?: string;
    parentResourceType?: number;
    parentResourceTypeValue?: string;
    parentResourceChildId?: string;
    resourceName?: string;
}

export interface IPrincipalPermissionPrincipal {
    childId?: string;
    firstLine?: string;
    secondLine?: string;
    picture?: string;
    principalTypeValue?: string;
    principalType?: number;
}

export interface IPrincipalPermission {
    areAllProcessesFinished?: boolean;
    werePermissionsApplied?: boolean;
    createdAt?: string;
    lastActiveAt?: string;
    role?: IPrincipalPermissionRole;
    type?: IPrincipalPermissionType;
    resource?: IPrincipalPermissionResource;
    principal?: IPrincipalPermissionPrincipal;
    assignedRoleId?: string;
}

export interface ICopyPermissionsRequest {
    targetPrincipalChildIds: string[];
    resourceChildId: string;
    updateOnlyToHigherPermissions?: boolean;
}

export interface ITransferPermissionsRequest {
    targetPrincipalChildId: string;
}

export type ClusterPermissions = EClusterPermissionsBase | EResourcePermissions;

export type OrganizationPermissions = EOrganizationPermissionsBase | EResourcePermissions;

export const normalizeWorkspaceInvitationApiRoleName = (apiRoleName: string): EExpandedOrganizationRoles | string => {
    switch (apiRoleName) {
        case 'workspace.Owner': return EExpandedOrganizationRoles.Owner;
        case 'workspace.Member': return EExpandedOrganizationRoles.Member;
        case 'workspace.Guest': return EExpandedOrganizationRoles.Guest;
        default: return apiRoleName;
    }
};